import React, { useRef, useState } from 'react';
import { Icon } from '@fikasio/react-ui-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAddObjectiveMutation } from 'services/objectives/api';
import { Objective } from 'services/objectives/types';
import { setPaneContent } from 'services/pane/slice';
import { matchesDueDatePreset } from 'components/filters/dueDatePreset';
import { RangeSelectEvent } from 'utils/rangeSelection';
import {
  applyObjectiveSelection,
  isClosedObjectiveStatus,
  objectiveMatchesAssignees,
  objectiveStatus,
} from './ObjectivesViewer/utils';
import ObjectivesFilters, {
  DEFAULT_OBJECTIVES_FILTER_DRAFT,
  ObjectivesFilterDraft,
} from './ObjectivesFilters/ObjectivesFilters';
import ObjectivesViewer from './ObjectivesViewer/ObjectivesViewer';
import SelectionBar from 'components/UI/SelectionBar';
import AddObjectiveButton from './AddObjectiveButton';

interface ObjectivesViewProps {
  onObjectiveClick: (objectiveId: string) => void;
  projectId?: string;
  showAddButton?: boolean;
  showCompletionFilter?: boolean;
  showDueDateFilter?: boolean;
  showViewModeButtons?: boolean;
  objectives?: Objective[] | null;
}

const ObjectivesView = ({
  objectives,
  onObjectiveClick,
  projectId,
  showAddButton,
  showCompletionFilter,
  showDueDateFilter,
  showViewModeButtons,
}: ObjectivesViewProps) => {
  const { t } = useTranslation();
  const [filterDraft, setFilterDraft] = useState<ObjectivesFilterDraft>(DEFAULT_OBJECTIVES_FILTER_DRAFT);
  const dispatch = useDispatch();
  const [selectedObjectives, setSelectedObjectives] = useState<Objective[]>([]);
  const lastSelectedObjectiveIdRef = useRef<string | undefined>(undefined);

  const [createObjective] = useAddObjectiveMutation();

  const addObjective = async objective => {
    return createObjective(objective);
  };

  const displayedObjectives = objectives?.filter(objective => {
    const status = objectiveStatus(objective);
    if (filterDraft.completion === 'ARCHIVED') {
      if (!objective.isArchived) return false;
    } else if (objective.isArchived) {
      return false;
    } else if (filterDraft.completion === 'COMPLETE' && status !== 'Completed') {
      return false;
    } else if (filterDraft.completion === 'INCOMPLETE' && isClosedObjectiveStatus(status)) {
      return false;
    } else if (filterDraft.completion === 'FAILED' && status !== 'Failed') {
      return false;
    } else if (filterDraft.completion === 'CANCELLED' && status !== 'Cancelled') {
      return false;
    }
    return matchesDueDatePreset(objective.dueDate, filterDraft.dueDate)
      && objectiveMatchesAssignees(objective, filterDraft.assignees);
  });

  const handleSelectObjective = (
    objective: Objective,
    event?: RangeSelectEvent,
    displayedObjectives: Objective[] = [],
  ) => {
    const result = applyObjectiveSelection({
      displayedObjectives,
      lastSelectedObjectiveId: lastSelectedObjectiveIdRef.current,
      objective,
      selectedObjectives,
      shiftKey: !!event?.shiftKey,
    });
    lastSelectedObjectiveIdRef.current = result.lastSelectedObjectiveId;
    setSelectedObjectives(result.selectedObjectives);
  };

  return (
    <>
      <ObjectivesViewer
        actions={(showAddButton || showCompletionFilter || showDueDateFilter) && (
          <>
            <ObjectivesFilters
              onApply={setFilterDraft}
              showCompletionFilter={showCompletionFilter}
              showDueDateFilter={showDueDateFilter}
              value={filterDraft}
            />
            {
              showAddButton && (
                <AddObjectiveButton
                  onClick={() => dispatch(
                    setPaneContent({
                      type: 'OBJECTIVE',
                      id: 'NEW',
                      ...(projectId ? { projectId } : {}),
                    }),
                  )}
                  style={{
                    lineHeight: '24px',
                  }}
                />
              )
            }
          </>
        )}
        objectives={displayedObjectives}
        onAddObjective={objective => addObjective(objective)}
        onObjectiveClick={onObjectiveClick}
        onObjectiveSelect={handleSelectObjective}
        onSelectAllObjectives={objectivesArray => {
          lastSelectedObjectiveIdRef.current = objectivesArray[objectivesArray.length - 1]?.id;
          setSelectedObjectives(objectivesArray);
        }}
        projectId={projectId}
        selectedObjectives={selectedObjectives}
        showViewModeButtons={showViewModeButtons}
      />
      {
        selectedObjectives.length > 0 && (
          <SelectionBar>
            <Icon
              name="times"
              onClick={() => {
                lastSelectedObjectiveIdRef.current = undefined;
                setSelectedObjectives([]);
              }}
              style={{
                cursor: 'pointer',
                marginRight: 10,
                width: 25,
              }}
            />
            <b>
              {t('xSelectedObjectives', { count: selectedObjectives.length })}
            </b>
          </SelectionBar>
        )
      }
    </>
  );
};

export default ObjectivesView;
